import { MAX_FLOW_ELEMENT_IDS } from "@redotech/customer-portal-sdk/rpc/schema/get-flow-element-text-overlay";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import {
  collectFlowElementIds,
  FlowElementTextOverlayContext,
} from "@redotech/redo-customer-portal-shared/flow-element-text";
import { DEFAULT_LOCALE, useTranslation } from "@redotech/redo-i18n";
import { FlowElementTextOverlay } from "@redotech/redo-return-core-common/return-flow/flow-element-translation";
import { localizationEnabled } from "@redotech/redo-team-common/localization/settings";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { memo, ReactNode, useContext, useMemo } from "react";
import { SettingsContext } from "./settings";

const EMPTY_OVERLAY: FlowElementTextOverlay = {};

/** Until the first response lands the overlay is empty, so the portal renders English. */
export const FlowElementTextOverlayProvider = memo(
  function FlowElementTextOverlayProvider({
    children,
  }: {
    children: ReactNode;
  }) {
    const settings = useContext(SettingsContext);
    const client = useContext(RpcClientContext);
    const { i18n } = useTranslation();
    const language = i18n.language.split("-")[0] ?? i18n.language;

    const elementIds = useMemo(
      () =>
        collectFlowElementIds([
          settings?.returnFlow,
          settings?.claimFlow,
          settings?.finalizeReturnFlow,
          settings?.finalizeClaimFlow,
          settings?.warrantyFlow,
        ]).slice(0, MAX_FLOW_ELEMENT_IDS),
      [
        settings?.returnFlow,
        settings?.claimFlow,
        settings?.finalizeReturnFlow,
        settings?.finalizeClaimFlow,
        settings?.warrantyFlow,
      ],
    );
    // Settings refreshes rebuild the flow objects, so key on the ids
    // themselves — an unchanged set is a cache hit, not another request.
    const elementIdsKey = elementIds.join("\n");

    const query = useQuery({
      queryKey: ["flowElementTextOverlay", language, elementIdsKey],
      queryFn: async () => {
        if (!client) {
          return EMPTY_OVERLAY;
        }
        const { overlay } = await client.getFlowElementTextOverlay({
          elementIds,
          language,
        });
        return overlay;
      },
      enabled:
        !!client &&
        localizationEnabled(settings?.localization) &&
        language !== DEFAULT_LOCALE &&
        elementIds.length > 0,
      // A failed or in-flight refetch keeps the text already on screen
      // instead of snapping the shopper back to English.
      placeholderData: keepPreviousData,
      // A merchant's translations cannot change mid-session, so one fetch per
      // language is enough -- without this, resolving auth refetches it.
      staleTime: Infinity,
    });

    return (
      <FlowElementTextOverlayContext.Provider
        value={query.data ?? EMPTY_OVERLAY}
      >
        {children}
      </FlowElementTextOverlayContext.Provider>
    );
  },
);
